/**
 * Cookie Consent Service
 * Manages user cookie preferences and consent status
 */

export interface CookiePreferences {
  necessary: boolean; // Always true - required for site functionality
  analytics: boolean; // Firebase Analytics
  advertising: boolean; // Google AdSense
  timestamp: number; // When consent was given
}

const CONSENT_KEY = 'zodiac_cookie_consent';
const CONSENT_VERSION = '1.0'; // Increment when policy changes

export class CookieConsentService {
  /**
   * Check if user has given consent
   */
  static hasConsent(): boolean {
    const consent = this.getConsent();
    return consent !== null;
  }

  /**
   * Check if user has consented to advertising cookies
   */
  static hasAdvertisingConsent(): boolean {
    const consent = this.getConsent();
    return consent?.advertising === true;
  }

  /**
   * Check if user has consented to analytics cookies
   */
  static hasAnalyticsConsent(): boolean {
    const consent = this.getConsent();
    return consent?.analytics === true;
  }

  /**
   * Get current consent preferences
   */
  static getConsent(): CookiePreferences | null {
    try {
      const stored = localStorage.getItem(CONSENT_KEY);
      if (!stored) return null;

      const data = JSON.parse(stored);

      // Check if consent version matches
      if (data.version !== CONSENT_VERSION) {
        // Policy changed - require new consent
        this.clearConsent();
        return null;
      }

      return data.preferences;
    } catch (error) {
      return null;
    }
  }

  /**
   * Save consent preferences
   */
  static saveConsent(preferences: Omit<CookiePreferences, 'timestamp'>): void {
    try {
      const consentData = {
        version: CONSENT_VERSION,
        preferences: {
          ...preferences,
          necessary: true, // Always true
          timestamp: Date.now(),
        },
      };

      localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));

      // Trigger storage event for other tabs
      window.dispatchEvent(new Event('storage'));

    } catch (error) {
    }
  }

  /**
   * Accept all cookies
   */
  static acceptAll(): void {
    this.saveConsent({
      necessary: true,
      analytics: true,
      advertising: true,
    });
  }

  /**
   * Accept only necessary cookies (reject optional)
   */
  static rejectAll(): void {
    this.saveConsent({
      necessary: true,
      analytics: false,
      advertising: false,
    });
  }

  /**
   * Clear consent (require new consent)
   */
  static clearConsent(): void {
    try {
      localStorage.removeItem(CONSENT_KEY);
    } catch (error) {
    }
  }

  /**
   * Listen for consent changes
   */
  static onConsentChange(callback: (preferences: CookiePreferences | null) => void): () => void {
    const handler = () => {
      callback(this.getConsent());
    };

    window.addEventListener('storage', handler);

    // Return cleanup function
    return () => {
      window.removeEventListener('storage', handler);
    };
  }
}
